import React, { useEffect, useCallback } from "react";
import { Wizard } from "@amzn/awsui-components-react";
import { IGenericDraftWizardProps, hasFormPersistence } from "./definitions";
import { useWizardState } from "./hooks/useWizardState";
import { useWizardConfig } from "./hooks/useWizardConfig";
import { handleWizardStepFormPersistence } from "utils/wizard-form-utils";
import styles from "./style.module.scss";

const GenericDraftWizard: React.FC<IGenericDraftWizardProps> = ({
  config,
  visible = true,
  onDismiss,
  initialData,
  isSubmitting: externalIsSubmitting = false,
}) => {
  const [state, actions] = useWizardState(0);
  const {
    steps,
    totalSteps,
    isValidStepIndex,
    getStepConfig,
    getSubmitButtonText,
    category,
    onComplete,
    onCancel,
    cancelButtonText,
  } = useWizardConfig(config);

  const {
    activeStepIndex,
    stepData,
    isSubmitting: internalIsSubmitting,
  } = state;
  const { setActiveStepIndex, setStepData, setIsSubmitting } = actions;

  useEffect(() => {
    if (config.setStepAdvancementCallback) {
      const handleStepAdvancement = (stepIndex: number) => {
        if (hasFormPersistence(config)) {
          handleWizardStepFormPersistence(config, activeStepIndex);
        }
        setActiveStepIndex(stepIndex);
      };
      config.setStepAdvancementCallback(handleStepAdvancement);
    }
  }, [config, setActiveStepIndex, activeStepIndex]);

  const effectiveIsSubmitting = externalIsSubmitting || internalIsSubmitting;

  useEffect(() => {
    if (initialData && Object.keys(stepData).length === 0) {
      setStepData(0, initialData);
    }
  }, [initialData, stepData, setStepData]);

  const handleNavigate = useCallback(
    (detail: { requestedStepIndex: number; reason: string }) => {
      const { requestedStepIndex, reason } = detail;

      if (effectiveIsSubmitting) {
        return;
      }

      if (
        reason === "next" &&
        activeStepIndex === 0 &&
        requestedStepIndex === 1
      ) {
        const currentStep = getStepConfig(activeStepIndex);
        if (
          currentStep?.validateWizardStep &&
          currentStep.triggerFormValidation
        ) {
          currentStep.triggerFormValidation();
          return;
        }
      }

      if (hasFormPersistence(config)) {
        handleWizardStepFormPersistence(config, activeStepIndex);
      }

      const isValid = isValidStepIndex(requestedStepIndex);

      if (isValid) {
        if (config.refetchDownloadUrl) {
          config.refetchDownloadUrl();
        }
        setActiveStepIndex(requestedStepIndex);
      }
    },
    [
      effectiveIsSubmitting,
      isValidStepIndex,
      setActiveStepIndex,
      getStepConfig,
      activeStepIndex,
      config,
    ],
  );

  const handleWizardSubmit = useCallback(async () => {
    if (effectiveIsSubmitting) {
      return;
    }

    if (onComplete) {
      setIsSubmitting(true);
      try {
        await onComplete(stepData);
        onDismiss?.();
      } catch (_error) {
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [effectiveIsSubmitting, onComplete, stepData, onDismiss, setIsSubmitting]);

  const handleWizardCancel = useCallback(() => {
    if (effectiveIsSubmitting) {
      return;
    }

    if (onCancel) {
      onCancel();
    } else {
      onDismiss?.();
    }
  }, [effectiveIsSubmitting, onCancel, onDismiss]);

  const wizardSteps = steps.map((stepConfig, _index) => ({
    title: stepConfig.title,
    description: stepConfig.description,
    content: stepConfig.content,
    isOptional: stepConfig.isOptional,
  }));

  if (!visible) {
    return null;
  }

  return (
    <div
      className={styles.genericDraftWizard}
      data-testid="generic-draft-wizard"
    >
      <Wizard
        i18nStrings={{
          stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, stepsCount) =>
            `Step ${stepNumber} of ${stepsCount}`,
          skipToButtonLabel: (step, _stepNumber) => `Skip to ${step.title}`,
          navigationAriaLabel: "Steps",
          cancelButton: cancelButtonText,
          previousButton: "Previous",
          nextButton: "Next",
          submitButton: effectiveIsSubmitting
            ? "Creating..."
            : getSubmitButtonText(activeStepIndex),
          optional: "optional",
        }}
        onNavigate={({ detail }) => handleNavigate(detail)}
        activeStepIndex={activeStepIndex}
        steps={wizardSteps}
        allowSkipTo={false}
        onCancel={!effectiveIsSubmitting ? handleWizardCancel : undefined}
        onSubmit={handleWizardSubmit}
        isLoadingNextStep={effectiveIsSubmitting}
      />
    </div>
  );
};

export default GenericDraftWizard;
